import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import DownloadHandle from "@/util/DownloadHandle";
import Metadata from "@/models/Metadata";
import Upload from "@/models/Upload";
import Vue from 'vue';
import * as Sentry from "@sentry/browser";
import EncryptedFile from "@/models/EncryptedFile";
import TransferInteractionConfiguration from "@/models/TransferInteractionConfiguration";

enum DownloadFormState {
    LOADING = 1,
    ASK_PASSWORD = 2,
    DECRYPT_META = 3,
    SHOW_DETAILS = 4,
    DOWNLOAD_PROGRESS = 5,
    SUCCESS = 6,
    ERROR = 7,
}

@Module
export default class DownloadStore extends VuexModule {
    status: DownloadFormState = DownloadFormState.LOADING;
    progress: number = 0;
    usesPassword: boolean = false;
    password: string = '';
    error: string = '';
    handle: DownloadHandle | null = null;
    browserSupportsBlobsize: boolean = false;
    receivedFiles: EncryptedFile[] = [];
    downloadProgressPerFile: object = {};

    interactionConfig: TransferInteractionConfiguration | undefined = undefined;

    //Comments:
    commentName: string = "";
    commentText: string = "";
    commentApproves: boolean = false;


    get showDownloadLoading() {
        return this.status === DownloadFormState.LOADING;
    }

    get showDownloadAskForPassword() {
        return this.status === DownloadFormState.ASK_PASSWORD;
    }

    get showDownloadDetails() {
        return this.status === DownloadFormState.SHOW_DETAILS;
    }

    get showDownloadError() {
        return this.status === DownloadFormState.ERROR;
    }

    get downloadErrorMessage() {
        return this.error;
    }

    get downloadProgress() {
        return this.progress;
    }

    get downloadMetadata() {
        return (this.handle && this.handle.getMeta()) ? this.handle.getMeta() : null;
    }

    @Mutation
    setCommentName(name: string) {
        this.commentName = name;
    }

    @Mutation
    setCommentText(text: string) {
        this.commentText = text;
    }

    @Mutation
    setCommentApproval(approval: boolean) {
        this.commentApproves = approval;
    }

    @Mutation
    startMetadataDownload(id: string) {
        this.handle = new DownloadHandle(id);
        this.status = DownloadFormState.LOADING;
        this.progress = 0;
        this.downloadProgressPerFile = {};
    }

    @Mutation
    switchOverMetadata(needsPassword: boolean) {
        if (needsPassword) {
            this.status = DownloadFormState.ASK_PASSWORD;
        } else {
            this.status = DownloadFormState.DECRYPT_META;
        }
        this.progress = 0;
    }

    @Action({ rawError: true })
    async finishMetadataDownload({needsPassword, urlMaterial}: {needsPassword: boolean, urlMaterial: string}) {
        this.context.commit("switchOverMetadata", needsPassword);
        if (!needsPassword) {
            await this.context.dispatch('decryptMetadata', {urlMaterial, password: ""});
        }
    }

    @Action
    enterPassword({password, urlMaterial}: {password: boolean, urlMaterial: string}) {
        this.status = DownloadFormState.DECRYPT_META;
        this.context.dispatch('decryptMetadata', {urlMaterial, password});
    }

    @Mutation
    setDownloadProgress(progress: number) {
        this.progress = progress;
    }

    @Mutation
    startFileDownload() {
        this.progress = 0;
    }

    @Mutation
    setDownloadError(message: string) {
        this.status = DownloadFormState.ERROR;
        this.progress = 0;
        this.error = message;
    }

    @Mutation
    setShowDownloadDetails() {
        this.status = DownloadFormState.SHOW_DETAILS;
        this.progress = 0;
    }
    @Mutation
    setFiles(files: EncryptedFile[]) {
        this.receivedFiles = files;
    }

    @Mutation
    setInteractionConfig(config: TransferInteractionConfiguration) {
        this.interactionConfig = config;
    }

    @Mutation
    setDownloadProgressForFile(payload: {fileName: string, percentDone: number}) {
        Vue.set(this.downloadProgressPerFile, payload.fileName, payload.percentDone);
    }

    @Mutation
    checkForBrowserSupport(filesize: number) {
        try {
            let arr: Uint8Array = new Uint8Array(filesize);
            let blob: Blob = new Blob([arr]);
            const works = blob.size === filesize;
            if (works) {
                Vue.$log.info(`Test successful. Constructed blob with ${blob.size} Bytes`);
                this.browserSupportsBlobsize = true;
            } else {
                Vue.$log.error(`Test failed. Constructed blob with ${blob.size} Bytes, Array had ${filesize}`);
                this.browserSupportsBlobsize = false;
            }
            //Overwrite reference, so it gets freed
            blob = new Blob();
            arr = new Uint8Array();
        } catch (e) {
            Vue.$log.info(`Test failed. Constructing blob with ${filesize} Bytes failed with exception`, e);
            this.browserSupportsBlobsize = false;
            Sentry.captureException(e);
        }
    }

    @Action({ rawError: true })
    async onlyDownloadMetadata({id, urlMaterial}: {id: string, urlMaterial: string}) {
        this.context.commit('startMetadataDownload', id);
        if (!this.handle) {
            // TODO: Error handling, TS wants it..
            return;
        }

        let result: Upload | undefined;
        let error;

        try {
            result = await this.handle.downloadMetadata();
        } catch (e) {
            error = e;
            Vue.$log.error("Metadata could not be downloaded, exception was thrown", e);
            this.context.commit('setDownloadError', "Dieser Link ist nicht mehr gültig.");
            Sentry.captureException(e);
            return;
        }

        if (!result) {
            Vue.$log.error("Metadata could not be downloaded", error);
            this.context.commit('setDownloadError', "Dieser Link ist nicht mehr gültig.");
            return;
        } else {
            await this.context.dispatch('finishMetadataDownload',
                {needsPassword: result.passwordProtected, urlMaterial });
            if (!result.passwordProtected) {
                await this.context.dispatch('prepareServiceWorker');
            }
            return;
        }
    }

    @Action({ rawError: true })
    async decryptMetadata({urlMaterial, password}: {urlMaterial: string, password: string}) {
        if (!this.handle) {
            // TODO: Error handling, TS wants it..
            return;
        }
        let result: boolean = false;
        if (password === "") {
            result = await this.handle.decryptMetadataWithKeyFromUrl(urlMaterial);
        } else {
            result = await this.handle.decryptMetadataWithPasswordAndUrlSalt(password, urlMaterial);
        }

        if (!result) {
            //this.context.commit('setDownloadError', "Metadata could not be decrypted");
            return false;
        }

        this.context.commit('setFiles', this.handle.getMeta().files);
        this.context.commit('setInteractionConfig', this.handle.getInteractionConfig());
        this.context.commit('setShowDownloadDetails');
        return true;
    }

    @Action({ rawError: true })
    async downloadFile(fileIdx: number) {
        this.context.commit('startFileDownload');
        if (!this.handle) {
            // TODO: Handle Error!
            return;
        }
        try {
            await this.handle.downloadFile(fileIdx, (percentDone) => {
                const fileName: string = this.receivedFiles[fileIdx].name;
                this.context.commit('setDownloadProgressForFile', {fileName, percentDone});
            });
        } catch (e) {
            this.context.commit('setDownloadError', e.message);
            Sentry.captureException(e);
        }
    }

    @Action({ rawError: true })
    async downloadFileViaSW() {
        this.context.commit('startFileDownload');
        if (!this.handle) {
            // TODO: Handle Error!
            return;
        }
        const self = this;
        navigator.serviceWorker.ready.then((serviceWorker) => {
            if (serviceWorker.active) {

                const keepalive = window.setInterval(() => {
                    if (serviceWorker.active) {
                        serviceWorker.active.postMessage({type: "keepalive"});
                        Vue.$log.debug("Keeping service worker alive by pinging...");
                    }
                }, 5000);

                function listenForProgress(evt: MessageEvent) {
                    const data = evt.data;
                    if (data.type === "progress" && typeof data.percentDone === "number") {
                        const percentDone: number = data.percentDone;
                        const fileName: string = data.fileName;
                        self.context.commit('setDownloadProgressForFile', {fileName, percentDone});
                    }
                }

                function listenForError(evt: MessageEvent) {
                    const data = evt.data;
                    if (data.type === "error") {
                        self.context.commit('setDownloadError', data.msg);
                        //navigator.serviceWorker.removeEventListener("message", listenForError);
                        //navigator.serviceWorker.removeEventListener("message", listenForProgress);
                        //navigator.serviceWorker.removeEventListener("message", listenForDone);
                    }
                }

                function listenForDone(evt: MessageEvent) {
                    const data = evt.data;
                    if (data.type === "done") {
                        if ( typeof data.fileName === "string") {
                            self.context.commit('setDownloadProgressForFile', {fileName: data.fileName, percentDone: 100});
                        }
                        //navigator.serviceWorker.removeEventListener("message", listenForError);
                        //navigator.serviceWorker.removeEventListener("message", listenForProgress);
                        //navigator.serviceWorker.removeEventListener("message", listenForDone);
                        window.clearInterval(keepalive);
                    }
                }

                navigator.serviceWorker.addEventListener("message", listenForError);
                navigator.serviceWorker.addEventListener("message", listenForProgress);
                navigator.serviceWorker.addEventListener("message", listenForDone);
            }
        });
    }

    @Action({ rawError: true })
    prepareServiceWorker() {
        const self = this;
        return new Promise((resolve, reject) => {


            navigator.serviceWorker.ready.then((serviceWorker) => {

                if (serviceWorker.active && self.handle) {
                    const meta: Metadata = self.handle.getMeta();
                    const uploadId: string = self.handle.getId();
                    const message: any = {type: "meta", meta, uploadId};
                    Vue.$log.debug("Sending metadata to worker", message);

                    //Handle ACK message, then the worker is ready:

                    function listenForAck(evt: MessageEvent) {
                        const data = evt.data;
                        if (data.type === "ACK") {
                            navigator.serviceWorker.removeEventListener("message", listenForAck);
                            //Start download:
                            Vue.$log.info(`ACK from serviceworker retrieved, ready to start download`);
                            resolve();
                        } else {
                            Vue.$log.warn(`No ACK from serviceworker`);
                        }
                    }

                    navigator.serviceWorker.addEventListener("message", listenForAck);

                    //Fire the metadata to the worker:
                    serviceWorker.active.postMessage(message);
                } else {
                    Vue.$log.fatal(`Serviceworker either not active or no handle`,
                        {active: serviceWorker.active, handle: self.handle});
                    reject();
                }
            });

        });
    }

}
